import React from 'react';
import PropTypes from 'prop-types';
import gettext from '@eb/gettext';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { loadReCaptcha } from 'react-recaptcha-v3';

import { withMainControls } from '@eb/eds-structure';

import { TYPE_ERROR } from '@eb/eds-notification';
import { AlertChunky } from '@eb/eds-iconography';
import { setWindowLocation } from '@eb/http';

import { BASE_URL } from '../../constants';

import PageHeader from '../../components/PageHeader';
import LogInForm from '../../components/forms/LogInForm';
import LinkedAccountDialog from '../../components/LinkedAccountsDialog';
import SplitContainer from '../../connectedComponents/SplitContainer';

/**
 * List of urls that will lead to this page:
 * * BASE_URL/SOCIAL_LOG_IN_PATH
 */
class SocialLoginPage extends React.PureComponent {
    static propTypes = {
        onLinkAccountsOnLogin: PropTypes.func,
        email: PropTypes.string,
        handleForgotPassword: PropTypes.func,
        moveBack: PropTypes.func,
        clearAuthNotification: PropTypes.func,
        recaptcha2PublicKey: PropTypes.string,
        recaptcha3PublicKey: PropTypes.string,
        disableRecaptcha3: PropTypes.bool,
        showCaptcha: PropTypes.bool,
        onSocialProviderClick: PropTypes.func,
        linkedAccountSuccessUrl: PropTypes.string,
    };
    state = {
        showDialog: false,
    };

    constructor(props) {
        super(props);

        this.dialogTimeout = null;
    }

    componentDidMount() {
        if (!this.props.disableRecaptcha3) {
            loadReCaptcha(this.props.recaptcha3PublicKey);
        }
    }
    componentDidUpdate(prevProps) {
        const { linkedAccountSuccessUrl } = this.props;
        if (
            linkedAccountSuccessUrl !== prevProps.linkedAccountSuccessUrl &&
            linkedAccountSuccessUrl
        ) {
            this.setState({
                showDialog: true,
            });
            this.dialogTimeout = setTimeout(
                () => this._handleCloseDialog(),
                5000,
            );
        }
    }
    // Don't have a good understanding of this function yet or when is it necessary.
    forceUpdate(socialProvider, stateFromProvider) {
        const { onSocialProviderClick } = this.props;

        if (onSocialProviderClick) {
            onSocialProviderClick({
                socialProvider,
                stateFromProvider,
            });
        }
    }

    _handleSubmit = (formData) => {
        const { clearAuthNotification, onLinkAccountsOnLogin } = this.props;

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        return onLinkAccountsOnLogin(formData);
    };

    _handleShowNotification = (error) => {
        const { addMainNotification } = this.props;

        /*Limit the call to addMainNotification to only if
        a notification hasn't been shown since last submit.*/
        if (!this._hasNotification) {
            addMainNotification({
                type: TYPE_ERROR,
                children: error,
                iconType: <AlertChunky />,
            });

            this._hasNotification = true;
        }
    };

    _handleClearNotification = () => {
        const { hideMainNotification } = this.props;

        this._hasNotification = false;
        hideMainNotification && hideMainNotification();
    };

    _handleForgotPassword = ({ sourceProvider }) => {
        const { handleForgotPassword } = this.props;

        // This is enabled only for apple only right now because only using siwa you can reach this page.
        handleForgotPassword &&
            handleForgotPassword({ sourceProvider, mustLinkAccount: true });
    };
    _handleCloseDialog = () => {
        const { linkedAccountSuccessUrl } = this.props;
        const redirectUrl = linkedAccountSuccessUrl
            ? linkedAccountSuccessUrl
            : BASE_URL;

        // If user triggered redirect manually, discard auto-redirect
        if (this.dialogTimeout) {
            clearTimeout(this.dialogTimeout);
        }

        this.setState({
            showDialog: false,
        });

        setWindowLocation(redirectUrl);
    };

    render() {
        const {
            recaptcha2PublicKey,
            recaptcha3PublicKey,
            showCaptcha,
            email,
            moveBack,
            authNotificationOptions,
            disableRecaptcha3,
            clearAuthNotification,
        } = this.props;

        let pageHeaderProps = {
            headerText: gettext('Used Eventbrite before?'),
            subtitleText: gettext(
                'If you have an existing Eventbrite account, enter your email and password to see all your tickets in one place.',
            ),
            yesButtonText: gettext('Yes, I have an account'),
            noButtonText: gettext("No, I'm new here"),
        };

        const buttonCTA = gettext('Link account');

        // hide subtitle when an auth notification is being displayed

        if (!isEmpty(authNotificationOptions)) {
            pageHeaderProps = omit(pageHeaderProps, 'subtitleText');
        }

        return (
            <SplitContainer header={<PageHeader {...pageHeaderProps} />}>
                <LinkedAccountDialog
                    showDialog={this.state.showDialog}
                    onCloseDialog={this._handleCloseDialog}
                />
                <LogInForm
                    buttonCTA={buttonCTA}
                    onSubmit={this._handleSubmit}
                    clearAuthNotification={clearAuthNotification}
                    handleEmailEdit={moveBack}
                    handleForgotPassword={this._handleForgotPassword}
                    recaptcha2PublicKey={recaptcha2PublicKey}
                    recaptcha3PublicKey={recaptcha3PublicKey}
                    disableRecaptcha3={disableRecaptcha3}
                    showCaptcha={showCaptcha}
                    email={email}
                    showNotification={this._handleShowNotification}
                    clearNotification={this._handleClearNotification}
                />
            </SplitContainer>
        );
    }
}

export default withMainControls(SocialLoginPage);
